<template>
  <div class="cage-verification-form">
    <div class="form-header">
      <h2 class="form-title">Verify Cage Number</h2>
      <p class="form-description">
        Please confirm the cage number matches the scanned animal's location.
      </p>
    </div>

    <div v-if="cageInfo" class="cage-info-display">
      <div class="info-item">
        <span class="info-label">Expected Cage Number:</span>
        <span class="info-value">{{ cageInfo.number }}</span>
      </div>
    </div>

    <form @submit.prevent="handleSubmit" class="verification-form">
      <div class="form-group">
        <label for="cage-number-input" class="form-label"> Enter Cage Number </label>
        <input
          id="cage-number-input"
          ref="cageNumberInputRef"
          v-model="cageNumberInput"
          type="text"
          class="form-input"
          :class="{ 'input-error': hasError }"
          placeholder="Enter 5-digit cage number"
          maxlength="5"
          pattern="[0-9]{5}"
          inputmode="numeric"
          autocomplete="off"
          @input="handleInput"
          @blur="validateCageNumber"
        />
        <div v-if="hasError" class="error-message">
          <strong>Mismatch:</strong> The entered cage number does not match the expected cage
          number.
        </div>
        <div v-if="isValid && cageNumberInput" class="success-message">✓ Cage number matches</div>
      </div>

      <div class="form-actions">
        <button type="submit" class="btn btn-primary" :disabled="!canProceed || isSubmitting">
          <span v-if="isSubmitting">Verifying...</span>
          <span v-else>Continue</span>
        </button>
        <button
          type="button"
          class="btn btn-secondary"
          @click="handleCancel"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
      </div>
    </form>

    <div v-if="showSupervisorPrompt" class="supervisor-prompt">
      <div class="prompt-content">
        <h3>Cage Number Mismatch</h3>
        <p>
          The entered cage number does not match the expected cage number. Please verify the cage
          number or contact a supervisor if you believe this is correct.
        </p>
        <div class="prompt-actions">
          <button
            type="button"
            class="btn btn-warning"
            @click="handleSupervisorOverride"
            :disabled="isSubmitting"
          >
            Override with Supervisor Approval
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            @click="handleCancel"
            :disabled="isSubmitting"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import type { CageInfo } from '../../stores/audit.store';

interface Props {
  cageInfo: CageInfo | null;
}

interface Emits {
  (e: 'verified', cageNumber: string): void;
  (e: 'cancel'): void;
  (e: 'supervisor-override', cageNumber: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const cageNumberInput = ref('');
const cageNumberInputRef = ref<HTMLInputElement | null>(null);
const hasError = ref(false);
const isValid = ref(false);
const isSubmitting = ref(false);
const showSupervisorPrompt = ref(false);

/**
 * Check if form can proceed
 */
const canProceed = computed(() => {
  if (!props.cageInfo) return false;
  if (!cageNumberInput.value.trim()) return false;
  return isValid.value && !hasError.value;
});

/**
 * Normalize cage number input (trim whitespace)
 */
function normalizeCageNumber(input: string): string {
  return input.trim();
}

/**
 * Validate cage number against expected cage number
 */
function validateCageNumber(): boolean {
  if (!props.cageInfo) {
    hasError.value = false;
    isValid.value = false;
    return false;
  }

  const normalizedInput = normalizeCageNumber(cageNumberInput.value);
  const expectedCageNumber = props.cageInfo.number.trim();

  if (!normalizedInput) {
    hasError.value = false;
    isValid.value = false;
    return false;
  }

  const matches = normalizedInput === expectedCageNumber;
  hasError.value = !matches;
  isValid.value = matches;

  return matches;
}

/**
 * Handle input changes
 */
function handleInput(): void {
  // Clear error state on input change
  if (hasError.value) {
    hasError.value = false;
    showSupervisorPrompt.value = false;
  }
  // Validate in real-time
  if (cageNumberInput.value.trim()) {
    validateCageNumber();
  } else {
    isValid.value = false;
  }
}

/**
 * Handle form submission
 */
async function handleSubmit(): Promise<void> {
  if (!canProceed.value || isSubmitting.value) {
    return;
  }

  const normalizedInput = normalizeCageNumber(cageNumberInput.value);
  if (!normalizedInput || !validateCageNumber()) {
    return;
  }

  isSubmitting.value = true;

  try {
    // Emit verified event
    emit('verified', normalizedInput);
  } catch (error) {
    console.error('Error verifying cage number:', error);
    hasError.value = true;
  } finally {
    isSubmitting.value = false;
  }
}

/**
 * Handle cancel
 */
function handleCancel(): void {
  emit('cancel');
}

/**
 * Handle supervisor override
 */
function handleSupervisorOverride(): void {
  const normalizedInput = normalizeCageNumber(cageNumberInput.value);
  if (!normalizedInput) {
    return;
  }

  emit('supervisor-override', normalizedInput);
}

/**
 * Watch for cage info changes
 */
watch(
  () => props.cageInfo,
  (newCageInfo) => {
    if (newCageInfo) {
      // Reset form when cage info changes
      cageNumberInput.value = '';
      hasError.value = false;
      isValid.value = false;
      showSupervisorPrompt.value = false;
    }
  },
  { immediate: true },
);

/**
 * Watch for validation errors to show supervisor prompt
 */
watch(hasError, (newHasError) => {
  if (newHasError && cageNumberInput.value.trim()) {
    // Show supervisor prompt after a brief delay if error persists
    setTimeout(() => {
      if (hasError.value && cageNumberInput.value.trim()) {
        showSupervisorPrompt.value = true;
      }
    }, 2000);
  } else {
    showSupervisorPrompt.value = false;
  }
});

// Focus input on mount
onMounted(() => {
  if (cageNumberInputRef.value) {
    cageNumberInputRef.value.focus();
  }
});
</script>

<style scoped>
.cage-verification-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-header {
  text-align: center;
}

.form-title {
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;
}

.form-description {
  color: #6b7280;
  margin: 0;
}

.cage-info-display {
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.info-label {
  font-weight: 600;
  color: #374151;
}

.info-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
}

.verification-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 600;
  color: #374151;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 1rem;
  transition: border-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input.input-error {
  border-color: #ef4444;
}

.form-input.input-error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.error-message {
  padding: 0.75rem;
  background-color: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #991b1b;
  font-size: 0.875rem;
}

.success-message {
  padding: 0.75rem;
  background-color: #d1fae5;
  border: 1px solid #a7f3d0;
  border-radius: 0.375rem;
  color: #065f46;
  font-size: 0.875rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.btn-warning {
  background-color: #f59e0b;
  color: white;
}

.btn-warning:hover:not(:disabled) {
  background-color: #d97706;
}

.supervisor-prompt {
  margin-top: 1rem;
  padding: 1.5rem;
  background-color: #fef3c7;
  border: 2px solid #fbbf24;
  border-radius: 0.5rem;
}

.prompt-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.prompt-content h3 {
  margin: 0;
  color: #92400e;
  font-size: 1.25rem;
}

.prompt-content p {
  margin: 0;
  color: #78350f;
}

.prompt-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}
</style>
